import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Stack, Divider } from "@mui/material";
import { Word } from "components/widgets/charts/WordCloud/WordCloudContent";
import TabbedDialog from "components/dialogs/TabbedDialog";
import WordCloudPreview from "./WordCloudPreview";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsSingleSelect, { OptionsValueType } from "components/settingsConfig/uiComponents/SettingsSingleSelect";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import { wordCloudDefaultSettings, WordCloudSettingsStateType } from "./WordCloudUtils";
import TopicQuerySettings from "components/widgets/utility/TopicQuerySettings";
import { ClusteringBias, normalizeClusteringBias } from "components/widgets/utility/ClusteringBias";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, getSingleWidgetResponseByName, WidgetRequest } from "system/widget/Widget";

const defaultWidgetRequest = {
  name: 'word-cloud',
  endpoint: 'query/topics',
  queryParams: { },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

export function BuildDefaultWordCloudRequests(): WidgetRequest[] | undefined  {
  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
  const requestInitBody = {
    topics: {
      size: 100,
      types: ["topic"]
    }
   };
  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}

export interface WordCloudSettings {  // made these optional so that the preview doesn't have to pass them all in
  dataKey?: keyof Word;
  colorKey?: keyof Word;
  spiralType?: string;
  wordCount?: number;
  padding?: number;
  excludedTerms?: string[];
  clusteringBias?: ClusteringBias;
}

export default function WordCloudSettings(props: WidgetSettingsProps): React.JSX.Element {
  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'word-cloud');
  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'word-cloud');
  const requestBody = originalRequest?.requestInit?.body;

  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>(requestBody?.topics?.fields || []);
  const [selectedTopicsTypes, setSelectedTopicsTypes] = useState<string[]>(requestBody?.topics?.types || []);
  const [sortBy, setSortBy] = useState<string>(requestBody?.topics?.sort || props.widget?.data?.sortBy || "relevance")
  const [queryTermExclusion, setQueryTermExclusion] = useState<string>(requestBody?.topics?.query_term_exclusion || "all");
  const [excludedTerms, setExcludedTerms] = useState<string[]>(props.widget?.data?.excludedTerms || []);
  const [clusteringBias, setClusteringBias] = useState<ClusteringBias>(normalizeClusteringBias(props.widget?.data?.clusteringBias));

  const [bulkExclusionDialogIsOpen, setBulkExclusionDialogIsOpen] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState<number>(0);
  const [settingsState, setSettingsState] = useState<WordCloudSettingsStateType>({
    ...wordCloudDefaultSettings,
    ...props.widget?.data?.settings,
  });

  const settingsOnChange = (updatedState: WordCloudSettingsStateType) => setSettingsState(updatedState);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  const { i18n } = useTranslation();

  const responseBody = apiResponseInfo?.responseBody || {};
  const apiData = responseBody?.topics ? responseBody?.topics.map((topic: any) => topic.label).sort() : [];

  const dataKeys = [
    { value: "appearances", label: i18n.t("requestOptions.field-types.appearances") },
    { value: "count", label: i18n.t("requestOptions.field-types.count") },
    { value: "distribution", label: i18n.t("requestOptions.field-types.distribution") },
    { value: "est_total_appearances", label: i18n.t("requestOptions.field-types.est-total-appearances") },
    { value: "est_total_count", label: i18n.t("requestOptions.field-types.est-total-count") },
    { value: "est_total_negative_count", label: i18n.t("requestOptions.field-types.est-total-negative-count") },
    { value: "est_total_positive_count", label: i18n.t("requestOptions.field-types.est-total-positive-count") },
    { value: "negative_count", label: i18n.t("requestOptions.field-types.negative-count") },
    { value: "positive_count", label: i18n.t("requestOptions.field-types.positive-count") },
    { value: "positivity", label: i18n.t("requestOptions.field-types.positivity") },
    { value: "relevance", label: i18n.t("requestOptions.field-types.relevance") },
    { value: "sentimental", label: i18n.t("requestOptions.field-types.sentimental") },
    { value: "velocity", label: i18n.t("velocity") }
  ];
  const spiralTypes: OptionsValueType[] = [
    { value: "archimedean", label: i18n.t("widget-types.word-cloud.settings.shape-types.oval") },
    { value: "rectangular", label: i18n.t("widget-types.word-cloud.settings.shape-types.rectangle") }
  ];
  const wordCountOptions = [
    { value: "10", label: "10" },
    { value: "20", label: "20" },
    { value: "30", label: "30" },
    { value: "40", label: "40" },
    { value: "50", label: "50" },
    { value: "75", label: "75" },
    { value: "100", label: `100 (${i18n.t("default")})` },
    { value: "200", label: `200 (${i18n.t("widget-settings.no-preview")})`, group: "preview not available" },
    { value: "500", label: `500 (${i18n.t("widget-settings.no-preview")})`, group: "preview not available" },
    { value: "1000", label: `1000 (${i18n.t("widget-settings.no-preview")})`, group: "preview not available" }
  ];
  const paddingOptions = [
    { value: 1, label: i18n.t("widget-types.word-cloud.settings.padding-types.compact") },
    { value: 5, label: i18n.t("widget-types.word-cloud.settings.padding-types.normal") },
    { value: 10, label: i18n.t("widget-types.word-cloud.settings.padding-types.spread" )}
  ];

  const updateWidget = () => {
    const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
    const requestInitBody: {
      topics: {
        size: number,
        types?: string[],
        fields?: string[],
        exclude?: string[],
        query_term_exclusion?: string, // "required" | "none" | "only"
        clustering_bias?: number,
        sort?: string,
      }
    } = {
      topics: {
        size: settingsState.wordCount,
      }
    };
    
    if (selectedTopicsTypes.length > 0) {
      requestInitBody.topics.types = selectedTopicsTypes;
    }
    if (selectedFieldIds.length > 0) {
      requestInitBody.topics.fields = selectedFieldIds;
    }
    if (sortBy !== "relevance") {
      requestInitBody.topics.sort = sortBy;
    }
    if (excludedTerms.length > 0) {
      requestInitBody.topics.exclude = excludedTerms;
    }
    if (queryTermExclusion !== "all") {
      requestInitBody.topics.query_term_exclusion = queryTermExclusion;
    }
    requestInitBody.topics.clustering_bias = clusteringBias;

    widgetRequest.requestInit = { body: requestInitBody };
    props.saveWidgetDataAndRequests({
      excludedTerms: excludedTerms,
      clusteringBias: clusteringBias,
      sortBy: sortBy,
      settings: settingsState
    }, [widgetRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    });
  }

  function handleSetSelectedFieldIds(value: string[]) {
    setSelectedFieldIds(value);
  }

  function handleSelectedTopicsTypes(value: string[]) {
    setSelectedTopicsTypes(value);
  }

  function handleSetExcludedTerms(values: string[]) {
    setExcludedTerms(values);
  }

  function handleSetBulkExclusionDialogIsOpen(value: boolean) {
    setBulkExclusionDialogIsOpen(value);
  }

  function handleSetQueryTermExclusion(value: string) {
    setQueryTermExclusion(value);
  }

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("widgets.settings.query-tab"), i18n.t("display")]}
      title={i18n.t("widget-types.word-cloud.settings.title")}>
      <>
        {/* Tab #1 */}
        {tabValue === 0 && (<>
          {props.queryInfo && (<>
            <TopicQuerySettings
              apiData={apiData}
              bulkExclusionDialogIsOpen={bulkExclusionDialogIsOpen}
              excludedTerms={excludedTerms}
              queryInfo={props.queryInfo}
              queryTermExclusion={queryTermExclusion}
              selectedFieldIds={selectedFieldIds}
              selectedTopicsTypes={selectedTopicsTypes}
              sortBy={sortBy}
              clusteringBias={clusteringBias}
              setBulkExclusionDialogIsOpen={handleSetBulkExclusionDialogIsOpen}
              setExcludedTerms={handleSetExcludedTerms}
              setQueryTermExclusion={handleSetQueryTermExclusion}
              setSelectedFieldIds={handleSetSelectedFieldIds}
              setSelectedTopicsTypes={handleSelectedTopicsTypes}
              setSortBy={setSortBy}
              setClusteringBias={setClusteringBias}
            />
          </>)}
        </>)}
        {/* Tab #2 */}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            {/* Word Cloud Preview */}
            <WordCloudPreview settingsState={settingsState} />

            {/* Settings Options */}
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              {/* Widget Title */}
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider={true}>
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                {settingsState.widgetTitle === 'custom' &&
                  <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                    <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                    <SettingsTextField
                      keyid="customWidgetTitle"
                      label="Custom Text"
                      value={settingsState.customWidgetTitle}
                    />
                  </Stack>}
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.widgetTitleAlignment} />
              </SettingsAccordion>

              {/* Size Field */}
              <SettingsSingleSelect
                defaultValue={dataKeys.find((item: OptionsValueType) => {
                  return item.value === "relevance";
                }) ?? dataKeys[0]}
                keyid="dataKey"
                label={i18n.t("size-field")}
                options={dataKeys}
                value={dataKeys.find((item: OptionsValueType) => {
                  return item.value === settingsState.dataKey;
                })} />

                {/* Color Field */}
                <SettingsSingleSelect
                  defaultValue={dataKeys.find((item: OptionsValueType) => {
                    return item.value === "positivity";
                  }) ?? dataKeys[0]}
                  keyid="colorKey"
                  label={i18n.t("color-field")}
                  options={dataKeys}
                  value={dataKeys.find((item: OptionsValueType) => {
                    return item.value === settingsState.colorKey;
                  })} />

              {/* Shape */}
              <SettingsSingleSelect
                defaultValue={spiralTypes[0]}
                keyid="spiralType"
                label={i18n.t("widget-types.word-cloud.settings.shape-types.shape")}
                options={spiralTypes}
                value={spiralTypes.find((item: OptionsValueType) => {
                  return item.value === settingsState.spiralType;
                })} />

              {/* Word Count */}
              <SettingsSingleSelect
                defaultValue={{value: "100", label: `100 (${i18n.t("default")})`}}
                keyid="wordCount"
                label={i18n.t("widget-types.word-cloud.settings.display-count")}
                options={wordCountOptions}
                value={wordCountOptions.find((item: OptionsValueType) => {
                  return item.value === settingsState.wordCount;
                })} />

              {/* Padding */}
              <SettingsSingleSelect
                defaultValue={paddingOptions.find((item: OptionsValueType) => {
                  return item.value === 5;
                }) ?? paddingOptions[0]}
                keyid="padding"
                label={i18n.t("widget-types.word-cloud.settings.padding-types.padding")}
                options={paddingOptions}
                value={paddingOptions.find((item: OptionsValueType) => {
                  return item.value === settingsState.padding;
                })} />
            </SettingsOptions>
          </SplitSettingsWrapper>}
      </>
    </TabbedDialog>
  )
}